import type AdmZip from 'adm-zip'
import fs from 'fs'
import path from 'path'

import { HASH_EXCLUDED_FOLDERS } from '../../constants'

// Turning a downloaded package back into a service folder.
//
// The archive's `package/` root IS the service, so nearly all of it belongs on disk — its source, its icons,
// and its own `package.json`. The one thing left behind is `node_modules`: it is generated, it is the bulk of
// the download, and `npm install` in the service folder rebuilds it from the lockfile that did come across.

const PACKAGE_PREFIX = 'package/'

/** A service id is a folder name and nothing else — no separators, no `..`, no absolute path. */
const SAFE_SERVICE_ID = /^[A-Za-z0-9][A-Za-z0-9._-]*$/

/**
 * Whether `serviceId` is safe to use as a path segment.
 *
 * The id arrives from the server, so it is input: `../../etc` as a service id would otherwise decide where
 * this command writes.
 */
export function isSafeServiceId(serviceId: string): boolean {
  return SAFE_SERVICE_ID.test(serviceId) && serviceId !== '.' && serviceId !== '..'
}

/** Everything in the archive that is worth writing — the service, minus what `npm install` regenerates. */
function serviceEntries(archive: AdmZip): AdmZip.IZipEntry[] {
  return archive.getEntries().filter(entry => {
    if (entry.isDirectory || !entry.entryName.startsWith(PACKAGE_PREFIX)) {
      return false
    }

    const [top] = entry.entryName.slice(PACKAGE_PREFIX.length).split('/')

    return !HASH_EXCLUDED_FOLDERS.includes(top)
  })
}

/**
 * Write the service's files into `directory`, returning how many were written.
 *
 * Entry names are confined to the target directory before anything is written: an archive is input, and a
 * `../` in a path is the oldest way to get a file written somewhere it was never meant to go.
 */
export function extractService(archive: AdmZip, directory: string): number {
  let written = 0

  for (const entry of serviceEntries(archive)) {
    const relativePath = entry.entryName.slice(PACKAGE_PREFIX.length)
    const target = path.resolve(directory, relativePath)

    if (target !== directory && !target.startsWith(directory + path.sep)) {
      continue
    }

    fs.mkdirSync(path.dirname(target), { recursive: true })
    fs.writeFileSync(target, entry.getData())

    written++
  }

  return written
}

/**
 * The dependencies the pulled service declares, if any.
 *
 * `node_modules` is not extracted, so a service that declares anything needs an install before it will run.
 * Reported rather than installed: running a package manager on someone's behalf, in a folder they have not
 * looked at yet, is a bigger act than unpacking it.
 */
export function declaredDependencies(archive: AdmZip): string[] {
  const raw = archive.getEntry(`${ PACKAGE_PREFIX }package.json`)?.getData().toString('utf-8')

  if (!raw) {
    return []
  }

  try {
    const manifest = JSON.parse(raw) as { dependencies?: Record<string, string> }

    return Object.keys(manifest.dependencies ?? {})
  } catch {
    // A manifest that will not parse tells us nothing, and must not fail a pull that already succeeded.
    return []
  }
}
